import { getDevices } from "../lib/api-admin";
import { sourceLabel, splitRows, summarizeDevices, versionLabel } from "../lib/devices";
import { int } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { DeviceActivityResponse } from "@apphq/shared/types";
import { AdoptionLine } from "./insights";
import { DeviceChart } from "./LazyCharts";
import { BarList, Card, EmptyState, ErrorState } from "./ui";
import { SkeletonRows } from "./skeletons";

/** Device numbers ignore the environment (sandbox / TestFlight devices are real), so the toggle does not refetch. */
export function useDevices(appId: string | null) {
  return useAsync(() => getDevices({ app_id: appId || undefined, period: "30d", environment: "production" }), [appId]);
}
type DevicesState = ReturnType<typeof useDevices>;

const EMPTY_TEXT = "Zodra een app met AppHQKit draait en een heartbeat stuurt, verschijnen hier de toestellen die de app nog hebben.";

/** Main card of Gebruik / app detail: big 7-day number, opened vs widget/background split and the 30-day line. */
export function ActiveOnPhoneCard({ state, title = "Actief op telefoon" }: { state: DevicesState; title?: string }) {
  const d = state.data;
  return (
    <Card title={title} aside={<span className="muted small">alleen App Store</span>}>
      {state.status === "error" && <ErrorState message={state.error} onRetry={state.reload} />}
      {state.status === "loading" && <SkeletonRows rows={4} icon={false} />}
      {state.status === "ready" && d && <ActiveOnPhoneBody d={d} />}
    </Card>
  );
}

function ActiveOnPhoneBody({ d }: { d: DeviceActivityResponse }) {
  if (d.totals.total_installs === 0) return <EmptyState title="Nog geen toestellen" icon="◔">{EMPTY_TEXT}</EmptyState>;
  const s = summarizeDevices(d.totals);
  return (
    <>
      <div className="device-hero">
        <div>
          <div className="big-number">{int(s.active)}</div>
          <div className="muted">toestellen met teken van leven in de afgelopen 7 dagen</div>
        </div>
        <div className="device-split">
          <div className="device-tile">
            <div className="kpi-label">Geopend</div>
            <div className="device-tile-value">{int(s.opened)}</div>
            <div className="muted small">{s.openedPercent}% van actief</div>
          </div>
          <div className="device-tile">
            <div className="kpi-label">Alleen widget/achtergrond</div>
            <div className="device-tile-value">{int(s.passive)}</div>
            <div className="muted small">app zelf niet geopend</div>
          </div>
        </div>
      </div>
      {d.series.length >= 2 ? (
        <DeviceChart points={d.series} />
      ) : (
        <p className="muted tight">De trendlijn bouwt zich op met de dagelijkse snapshot; vanaf de tweede dag verschijnt de grafiek.</p>
      )}
    </>
  );
}

/** Splits of the devices active in the last 7 days. */
export function DeviceSplits({ state }: { state: DevicesState }) {
  const d = state.data;
  if (!d || d.totals.active_7d === 0) return null;
  const blocks: Array<{ title: string; rows: ReturnType<typeof splitRows> }> = [
    { title: "Laatste teken van leven via", rows: splitRows(d.by_source, sourceLabel) },
    { title: "iOS-versie", rows: splitRows(d.by_os_version, versionLabel) },
    { title: "Appversie", rows: splitRows(d.by_app_version, versionLabel) },
  ];
  // adoption comes pre-computed from the API (full split); by_app_version is top-8 + "other" and only feeds the list below
  return (
    <>
      {d.adoption && (d.adoption.app_version || d.adoption.os_version) && (
        <Card title="Adoptie nieuwste versie">
          <AdoptionLine appVersion={d.adoption.app_version} osVersion={d.adoption.os_version} />
        </Card>
      )}
      <div className="grid-3">
        {blocks.map((b) => (
          <Card key={b.title} title={b.title}>
            {b.rows.length === 0 ? <EmptyState title="Geen gegevens" /> : <BarList rows={b.rows} format={int} />}
          </Card>
        ))}
      </div>
    </>
  );
}
